import { parseQuizPoint, MS_PER_DAY } from '../../utils/performance-trend';

export const QUESTION_SELECTIONS = [
  { value: 'FRESH', label: 'New to this student' },
  { value: 'INCORRECT', label: 'Review missed questions' },
  { value: 'ALL', label: 'Include previous questions' },
];
export const FRESH_QUESTION_HELP = 'New excludes recorded attempts and questions marked covered together. Earlier unsaved sessions are not included.';

// Practice suggestions are a lower bar than readiness (READINESS_DEFAULTS stays
// untouched): a wider window, but the same 20-question count floor per topic
// plus two separate quizzes, so one off day cannot drive a suggestion.
// targetTopics is a target, not a hard cap: topics that tie the last one kept
// exactly (fed by the same quizzes, say) are kept with it rather than cut by name.
export const PRACTICE_SUGGESTION_POLICY = {
  windowDays: 90,
  maxTopicsPerQuiz: 3,
  minQuizzes: 2,
  minQuestions: 20,
  targetTopics: 3,
};

// Assignment provenance does not mean the student received help: tutor-assigned
// quizzes completed alone count. Tutor mode and done-together sessions do not.
const isIndependentCompletion = (row) =>
  row?.iscomplete === true && row.tutorOn !== true && !row.completedWithTutorId;

// A missing examType is default-filled with every exam on the server, and a
// mixed-exam score cannot be credited to one exam, so only single-exam quizzes count.
const isOnlyExam = (point, exam) => new Set(point.examTypes).size === 1 && point.examTypes[0] === exam;

// History rows only carry the quiz's selection filter and a whole-quiz score;
// there is no per-question discipline. A whole-exam quiz lists every discipline,
// so only quizzes aimed at a few topics can say anything about one of them.
const quizTopics = (point) => [...new Set(point.disciplines.filter(name => typeof name === 'string' && name))];

const byLowerScore = (a, b) =>
  a.correct * b.questions - b.correct * a.questions || b.questions - a.questions || a.name.localeCompare(b.name);
const sameEvidence = (a, b) => a.questions === b.questions && a.correct === b.correct;

// The ranked list only holds qualifying catalog topics, so the extension is
// bounded by the catalog.
const keepTarget = (ranked, target) => {
  let end = Math.min(target, ranked.length);
  while (end > 0 && end < ranked.length && sameEvidence(ranked[end], ranked[end - 1])) end += 1;
  return ranked.slice(0, end);
};

export function practiceSuggestions(rows, exam, available, now = Date.now(), policy = PRACTICE_SUGGESTION_POLICY) {
  const start = now - policy.windowDays * MS_PER_DAY;
  const catalog = new Set(Array.isArray(available) ? available : []);
  const completed = (Array.isArray(rows) ? rows : [])
    .filter(row => row?.iscomplete === true)
    .map(row => ({ independent: isIndependentCompletion(row), point: parseQuizPoint(row) }))
    .filter(({ point }) => point && isOnlyExam(point, exam));
  const independent = completed.filter(entry => entry.independent && entry.point.time <= now).map(entry => entry.point);
  const recent = independent.filter(point => point.time > start);
  const focused = recent.filter(point => {
    const count = quizTopics(point).length;
    return count > 0 && count <= policy.maxTopicsPerQuiz;
  });

  const totals = new Map();
  focused.forEach(point => quizTopics(point).filter(name => catalog.has(name)).forEach(name => {
    const entry = totals.get(name) || { name, quizzes: 0, questions: 0, correct: 0 };
    entry.quizzes += 1;
    entry.questions += point.total;
    entry.correct += point.correct;
    totals.set(name, entry);
  }));
  const ranked = [...totals.values()]
    .filter(entry => entry.quizzes >= policy.minQuizzes && entry.questions >= policy.minQuestions)
    .sort(byLowerScore);
  const topics = keepTarget(ranked, policy.targetTopics)
    .map(entry => ({ ...entry, accuracy: Math.round((entry.correct / entry.questions) * 100) }));

  return {
    exam,
    windowDays: policy.windowDays,
    quizzes: recent.length,
    focusedQuizzes: focused.length,
    topics,
    lastIndependentAt: independent.length ? Math.max(...independent.map(point => point.time)) : null,
    // Only decides whether to offer the existing INCORRECT selection, which the
    // server serves from every recorded miss; it never ranks topics.
    hasMissedQuestions: completed.some(({ point }) => point.correct < point.total),
  };
}

export function practicePoolError(result) {
  const raw = result?.errMsg;
  const message = typeof raw === 'string' ? raw : raw?.message;
  if (typeof message !== 'string') return '';
  const match = message.match(/Only (\d+) matching questions remain\./);
  return match ? `Only ${match[1]} matching questions remain. Choose fewer questions, broaden topics, or include previous questions.` : '';
}
